import { getCookie } from '@nykaa/utils/cookies';
import { PIN_CODE_FOR_DELIVERY } from '@nykaa/delivery-shared/constants';
import { Variant, PrimaryCategories } from 'store/product/types';
import { pushEvent } from 'helpers/utils/dataLayer';
import { CartData, AddToCartClickedData } from './types';
import { events, PageSource, pageTypes } from 'shared/constants/dataLayer';
import { getFeaturedString } from 'shared/store/dataLayer/helpers';
import { PageJourney } from 'shared/store/dataLayer/action';
import { PageLocationType } from 'shared/store/categoryListing/types';
import { CURRENCY, NO_OFFER_ID, NO_OFFER_MESSAGE } from 'shared/constants';
import { OfferData } from 'types/productCard';
import PageTypes from 'constants/pageTypes';
import { ADDRESS_ID_FOR_DELIVERY } from 'layouts/Product/DeliverySection/constant';
import { pushData } from '@nykaa/data-layer';
import { ProductWidgetTrackingProps } from 'components/CartOperations';
import { transformDataforMixPanelTracking } from 'components/CartOperations/helper';
import { UrgencyTemplate } from '@nykaa/ui-kit/component/PriceFluctuationNudge/types';

const pushAddToCart = (
  data: AddToCartClickedData,
  journeyData: PageJourney | undefined
): void => {
  const { clickInteraction = '' } = data;
  const { plpPriceRevealCart = '' } = data;
  const cardEventData = {
    cartAddition: data,
  };
  if (journeyData) {
    cardEventData.siteNavigation = journeyData.siteNavigation;
    cardEventData.siteSubNavigation = journeyData.siteSubNavigation;
    cardEventData.sourceSubSiteNavigation = journeyData.sourceSubSiteNavigation;
  }
  const eventData = {
    cardEventData,
    cartAddition: {
      pageSource: data?.pageSource,
      productId: data?.productId,
    },
    ...(clickInteraction && { clickInteraction }),
    ...{ plpPriceRevealCart },
  };
  if (journeyData?.siteNavigation && journeyData?.siteSubNavigation) {
    eventData.siteNavigation = journeyData.siteNavigation;
    eventData.siteSubNavigation = journeyData.siteSubNavigation;
  }
  if (journeyData?.sourceSubSiteNavigation) {
    eventData.sourceSubSiteNavigation = journeyData.sourceSubSiteNavigation;
  }
  pushEvent(events.ADD_TO_CART_CLICK, eventData);
};

// TODO: add a type for data
const pushAddToCartSuccess = (
  data: any,
  productWidgetProps?: ProductWidgetTrackingProps,
  appTrackingKeys?: Record<string, string>,
  productData?: any
) => {
  const {
    offerMessage = NO_OFFER_MESSAGE,
    offerId = NO_OFFER_ID,
    showOffer = false,
    clickInteraction = '',
  } = data;
  const offerData: OfferData = {};
  if (showOffer) {
    offerData.offerMessage = offerMessage || NO_OFFER_MESSAGE;
    offerData.offerId = offerId || NO_OFFER_ID;
  }
  const payload = {
    cartAdditionSuccess: data,
    ...offerData,
    ...(clickInteraction && { clickInteraction }),
  };
  pushEvent(events.ADD_TO_CART_SUCCESS, payload);
  if (data?.isConvSearch) {
    if (appTrackingKeys) {
      const {
        interaction_location,
        search_term,
        site_navigation,
        site_sub_navigation,
        ui_design_version,
        active_tab,
      } = appTrackingKeys || {};
      const { primaryCategories } = productData || {};
      const productCategoryL1 = primaryCategories?.l1?.name || '';
      const productCategoryL2 = primaryCategories?.l2?.name || '';
      const productCategoryL3 = primaryCategories?.l3?.name || '';
      payload['interaction_location'] = interaction_location;
      payload['listing_type'] = 'searchListingPage';
      payload['listing_opened'] = `Search:query:${search_term}`;
      payload['listing_value'] = search_term;
      payload['site_navigation'] = site_navigation;
      payload['site_sub_navigation'] = site_sub_navigation;
      payload['search_keyword'] = search_term;
      payload['category_l1_name'] = productCategoryL1;
      payload['category_l2_name'] = productCategoryL2;
      payload['category_l3_name'] = productCategoryL3;
      payload['ui_design_version'] = ui_design_version;
      payload['active_tab'] = active_tab;
      payload['plp_click_count'] =
        window.datalayer?.plpInteractionData?.clickCount || 0;
    }
  }
  transformDataforMixPanelTracking(
    payload,
    'cartAdditionSuccess',
    productWidgetProps
  );
};

interface Product extends Variant {
  parentId: string;
  brandName: string;
  primaryCategories: PrimaryCategories;
  price: number;
  isLux: number;
  isMini?: boolean;
  tags: string;
  offerMessage: string;
  offerId: string;
  urgencyTemplate: UrgencyTemplate;
  secondaryTagName: string;
  isOfferPresent: boolean;
  callOutsTemplateV2: any;
}
interface TrackAddToCart {
  product: Product;
  productData?: any;
  cartData: CartData; // TODO: add a type for this
  journeyData?: PageJourney;
  pageSource?: string;
  videoId?: string;
  actionSource?: string;
  clickInteraction?: string;
  trackingData?: string;
  plpPriceRevealCart?: string;
  gptConversationId?: string;
  isConvSearch?: boolean;
  appTrackingKeys?: Record<string, string>;
  enableSecondaryTagTracking?: boolean;
  updated_quantity?: number;
}
export const trackAddToCart = (
  {
    product,
    productData,
    cartData,
    journeyData,
    pageSource,
    videoId,
    actionSource,
    clickInteraction,
    trackingData,
    plpPriceRevealCart,
    gptConversationId,
    isConvSearch,
    appTrackingKeys,
    enableSecondaryTagTracking = false,
    updated_quantity,
  }: TrackAddToCart,
  productWidgetProps?: ProductWidgetTrackingProps,
  plpInteractionData?: Record<string, any>
) => {
  const {
    offerMessage = NO_OFFER_MESSAGE,
    offerId = NO_OFFER_ID,
    showOffer = false,
  } = product;
  if (product) {
    let productImageUrl = '';
    const productMedia = product.media?.filter(
      ({ mediaType }) => mediaType === 'image'
    );
    if (productMedia && productMedia.length > 0) {
      productImageUrl = productMedia[0]?.url || '';
    }
    const {
      isPrEnabled = false,
      isEarlyAccess = false,
      isFlashSale = false,
    } = product;
    pushData({
      saleConfig: {
        isPrEnabled,
        isEarlyAccess,
        isFlashSale,
      },
    });
    pushAddToCart(
      {
        brandName: product.brandName,
        variantName: product.name,
        productId: product.childId,
        offerPrice: product.offerPrice || product.price,
        mrp: product.mrp,
        quantity: 1,
        productImageUrl: productImageUrl,
        categoryNames: [null, 'p'], // FIXME: need to change GTM Logic to pick this from current pageName
        featured: getFeaturedString(product.featured),
        categoryLevel: product.primaryCategories,
        isLux: product.isLux,
        tags: product?.tags,
        pageSource,
        actionSource,
        trackingData,
        videoId,
        clickInteraction,
        plpPriceRevealCart,
      },
      journeyData,
      productWidgetProps
    );

    const position = cartData.items?.findIndex(
      (cartItem) => cartItem.productId === product.childId
    );
    const variants = productData?.variants;
    const inStockVariants = variants?.filter(
      (item) => item.inStock === true
    ).length;
    const oosVariants = variants?.length - inStockVariants;
    const differentialPrice =
      product?.cohortSaleTemplateV2?.price ||
      product?.cohortSaleTemplate?.price ||
      product.offerPrice;
    const differentialDiscount =
      product?.cohortSaleTemplateV2?.discountPercentage ||
      product?.cohortSaleTemplate?.discount ||
      product.discount;
    const differentialPriceTempId =
      product?.cohortSaleTemplateV2?.templateId ||
      product?.cohortSaleTemplate?.templateId ||
      '';
    let secondaryTagEligible;
    let secondaryTagV2Visible;
    if (enableSecondaryTagTracking) {
      const experimentEnabled = window.datalayer?.isTagsV2Enabled || false;
      secondaryTagEligible = product?.secondaryTagV2;
      secondaryTagV2Visible = experimentEnabled && secondaryTagEligible;
    }
    const productExtraData = {
      parentId: product.parentId,
      packSize: product.packSize,
      isMini: product.isMini || false,
      childId: product.childId,
      id: product.parentId,
      categoryLevel: product.primaryCategories,
      productType: product.productType,
      inStock: product.inStock,
      brandName: product.brandName,
      tags: product.tags || productData?.tags,
      discount: product.discount,
      mrp: product.mrp,
      offerPrice: product.offerPrice || productData?.price,
      type: product.type || productData?.productType || productData?.type,
      variantCount: product.variantCount || productData?.variants?.length,
      variantType: product.variantType || productData?.variantType,
      inStockVariants,
      oosVariants,
      cohortSaleTemplate: product.cohortSaleTemplate,
      cohortSaleTemplateV2: product.cohortSaleTemplateV2,
      urgencyTemplate: product.urgencyTemplate,
      ...((product.cohortSaleTemplate || product.cohortSaleTemplateV2) && {
        isDifferentialPricing: true,
        differentialPrice,
        differentialDiscount,
        differentialPriceTempId,
      }),
      secondaryTagName: product.secondaryTagName || '',
      isOfferPresent: product.isOfferPresent || false,
      callOutsTemplateV2: product.callOutsTemplateV2,
      bestPrice: product.bestPrice,
      secondaryTagV2Eligible: enableSecondaryTagTracking
        ? !!secondaryTagEligible
        : undefined,
      secondaryTagV2Visible: enableSecondaryTagTracking
        ? !!secondaryTagV2Visible
        : undefined,
    };
    pushAddToCartSuccess(
      {
        grandTotal: cartData.grandTotal,
        cartItems: cartData.items.map((cartItem) => ({
          id: cartItem.productId,
          productId: cartItem.productId,
          quantity: cartItem.quantity,
          sku: cartItem.sku,
          parentId: product.parentId,
          offerPrice: cartItem.offerPrice,
          productName: cartItem.name,
          qty: cartItem.quantity,
        })),
        shipping: cartData.shipping,
        position,
        offerMessage,
        offerId,
        showOffer,
        product: {
          ...cartData.items[position],
          trackingData,
          ...productExtraData,
        },
        currency: CURRENCY,
        clickInteraction,
        gptConversationId,
        isConvSearch,
        ...(updated_quantity !== undefined && { updated_quantity }),
      },
      productWidgetProps,
      appTrackingKeys,
      productData,
      plpInteractionData
    );
  }
};

interface PageSourceProps {
  pageLocation: PageLocationType;
  variantType?: string;
  productId?: string;
  isPreview?: boolean;
}
export const getPageSource = ({
  pageLocation = {},
  variantType,
  productId,
  isPreview,
}: PageSourceProps): string => {
  let pageSource = '';
  const pageType = pageLocation?.pageType || '';
  const listingPageType = pageLocation?.listingPageType || '';
  const categoryId = pageLocation?.id || '';
  switch (pageType) {
    case PageSource.CAB_WIDGET:
    case PageSource.CAV_WIDGET:
      if (pageLocation.actionSource) {
        pageSource = pageLocation.actionSource;
      } else {
        if (isPreview) {
          pageSource = `Nykaa:${pageType}:${variantType}:${productId}`;
          return pageSource;
        }
        pageSource = `Nykaa:${pageType}:${productId}`;
      }
      return pageSource;
    case PageSource.Product:
      if (pageLocation.actionSource) {
        pageSource = pageLocation.actionSource;
      } else {
        if (isPreview) {
          pageSource = `Nykaa: pdp: ProductDetailPage: ${variantType}`;
          return pageSource;
        }
        pageSource = 'Nykaa: pdp: ProductDetailPage';
      }
      return pageSource;

    case PageSource.SEARCH:
    case PageSource.CATEGORY: {
      const isSearch = pageType === PageSource.SEARCH;
      if (isPreview) {
        if (isSearch) {
          pageSource = `Nykaa:${pageType}:${variantType}`;
          return pageSource;
        }
        pageSource = `Nykaa:${pageType}:${variantType}:${listingPageType}:${categoryId}`;
        return pageSource;
      }
      if (isSearch) {
        pageSource = `Nykaa: ${pageType}`;
      } else {
        pageSource = `Nykaa: ${pageType}:${listingPageType}:${categoryId}`;
      }
      return pageSource;
    }
    case PageSource.SIZE_CHART:
      pageSource = pageTypes.SIZE_CHART;
      return pageSource;
    case PageSource.VIDEO_FULL_SCREEN:
      pageSource = `Nykaa:ProductDetailPage:${pageType}`;
      return pageSource;
    case PageSource.IMAGE_FULL_SCREEN:
      pageSource = `Nykaa:ProductDetailPage:${pageType}`;
      return pageSource;
    case PageSource.RELATED_WIDGETS:
      pageSource = `nykaa:${pageType}`;
      return pageSource;
  }
};

export const getPageName = (pageType: string) => {
  switch (pageType) {
    case PageTypes.PRODUCT:
      return 'pdp_page';

    case PageTypes.HOME:
      return 'home';

    case PageTypes.CATEGORY:
    case PageTypes.LISTING:
    case PageTypes.SEARCH_LISTING:
      return 'plp_page';

    default:
      return '';
  }
};

export const getEddPinCode = () => {
  const isAddress = getCookie(ADDRESS_ID_FOR_DELIVERY).toString() || '';
  if (!isAddress) {
    return {
      pincode: getCookie(PIN_CODE_FOR_DELIVERY).toString() || '',
    };
  }
  return {};
};
